import React, { useState } from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Dialog from '@afosto/app-components-react/Dialog';
import LoadingWrapper from '@afosto/components-react/LoadingWrapper';
import { tenants as tenantsApi } from '@afosto/iam/api/Tenants';
import translations from './translations';

const DOMAINS_TO_PURGE = [
  'lcs:shipments',
  'odr:orders',
  'odr:invoices',
  'odr:calculations',
  'odr:transactions',
];

const PurgeRequestDialog = ({ isOpen, onClose }) => {
  const intl = useIntl();
  const [isRequestingPurge, setIsRequestingPurge] = useState(false);
  const [purgeRequestResponse, setPurgeRequestResponse] = useState(null);

  const handleRequestPurge = async () => {
    setIsRequestingPurge(true);

    try {
      const response = await tenantsApi.requestPurgeToken({
        domains: DOMAINS_TO_PURGE,
      });
      const { data } = response;

      setPurgeRequestResponse(data);
    } catch (error) {
      setPurgeRequestResponse(null);
    } finally {
      setIsRequestingPurge(false);
    }
  };

  const handleOnClose = () => {
    if (onClose && typeof onClose === 'function') {
      onClose();
    }
  };

  const handleOnExited = () => {
    setPurgeRequestResponse(null);
  };

  return (
    <Dialog onClose={handleOnClose} onExited={handleOnExited} open={isOpen}>
      <LoadingWrapper isLoading={isRequestingPurge}>
        {purgeRequestResponse === null ? (
          <>
            <DialogTitle>
              <Typography variant="h6">{intl.formatMessage(translations.title)}</Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="body2" paragraph>
                {intl.formatMessage(translations.description)}
              </Typography>
              <Typography variant="overline">
                {intl.formatMessage(translations.dataToPurge)}
              </Typography>

              <Grid container spacing={1}>
                {DOMAINS_TO_PURGE.map(domain => (
                  <Grid item>
                    <Chip label={domain} color="primary" variant="outlined" />
                  </Grid>
                ))}
              </Grid>
            </DialogContent>
            <DialogActions style={{ justifyContent: 'space-between' }}>
              <Button onClick={handleOnClose} variant="outlined" disabled={isRequestingPurge}>
                {intl.formatMessage(translations.actions.cancel)}
              </Button>
              <Button
                onClick={handleRequestPurge}
                variant="contained"
                color="secondary"
                disabled={isRequestingPurge}
              >
                {intl.formatMessage(translations.actions.requestPurge)}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>
              <Typography variant="h6">{intl.formatMessage(translations.successTitle)}</Typography>
            </DialogTitle>
            <DialogContent>
              {intl.formatMessage(translations.successDescription, {
                signee: purgeRequestResponse?.email,
              })}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleOnClose} variant="contained" color="primary">
                {intl.formatMessage(translations.actions.close)}
              </Button>
            </DialogActions>
          </>
        )}
      </LoadingWrapper>
    </Dialog>
  );
};

PurgeRequestDialog.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
};

PurgeRequestDialog.defaultProps = {
  isOpen: false,
  onClose: undefined,
};

export default PurgeRequestDialog;
